import React, { useState } from 'react';
import { Copy, Download, Link } from 'lucide-react';
import { notify } from '../../utils/toast';
import { useTheme } from '../../hooks';
// import { useAuth } from '../../hooks/chat/useAuth';
import {  getAuthData } from "../../contexts/AuthContext";
import { getAiApiUrl } from '../../config';
import QRCode from 'qrcode';

interface ShareQRCodeDialogProps {
  conversationId: string;
  onClose: () => void;
  onSuccess?: () => Promise<void>;
}

export const ShareQRCodeDialog: React.FC<ShareQRCodeDialogProps> = ({
  conversationId,
  onClose,
  onSuccess
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [sharedUrl, setSharedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { theme } = useTheme();

  
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  // Use a ref to track if we've already made the API call
  const apiCallMadeRef = React.useRef(false);

  React.useEffect(() => {
    // Only make the API call once
    if (apiCallMadeRef.current) return;
    apiCallMadeRef.current = true;

    const shareConversation = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const auth = await getAuthData();
        const response = await fetch(`${getAiApiUrl()}/share-convo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.jwt}`
          },
          body: JSON.stringify({
            walletAddress: auth.walletAddress,
            nonce: auth.nonce,
            conversation_id: conversationId,
            user: auth.did.toLowerCase().replaceAll('_uuid', ':uuid').replaceAll('_addr', ':addr').replaceAll('_asa', ':asa').replaceAll('_app', ':app').replaceAll('_txn', ':txn')
          })
        });

        if (!response.ok) {
          throw new Error('Failed to share conversation');
        }

        const data = await response.json();
        
        // Assuming the API returns a URL for the shared conversation
        const sharedConvoUrl = data.url || `${window.location.origin}/convo/shared/${data.id || conversationId}`;
        setSharedUrl(sharedConvoUrl);
        
        // Generate QR code
        const qrDataUrl = await generateQRCode(sharedConvoUrl);
        setQrCodeDataUrl(qrDataUrl);
        
        // Call onSuccess callback to update conversation state
        if (onSuccess) {
          await onSuccess();
        }
      } catch (error) {
        console.error('Error sharing conversation:', error);
        setError('Failed to share conversation. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    shareConversation();
  }, [conversationId]); // Remove getAuthData from dependencies

  const generateQRCode = async (url: string) => {
    const qrSize = 400;
    
    // Generate QR code
    const qrDataUrl = await QRCode.toDataURL(url, {
      width: qrSize,
      margin: 2,
      color: {
        dark: isDark ? '#ffffff' : '#000000',
        light: isDark ? '#1f2937' : '#ffffff'
      },
      errorCorrectionLevel: 'H'
    });

    // Create a canvas to draw the QR code with a logo
    const canvas = document.createElement('canvas');
    canvas.width = qrSize;
    canvas.height = qrSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');

    // Draw QR code on canvas
    const qrImage = new Image();
    await new Promise((resolve, reject) => {
      qrImage.onload = resolve;
      qrImage.onerror = reject;
      qrImage.src = qrDataUrl;
    });
    ctx.drawImage(qrImage, 0, 0, qrSize, qrSize);

    // Draw logo
    const logoSize = 80;
    const logo = new Image();
    await new Promise((resolve, reject) => {
      logo.onload = resolve;
      logo.onerror = reject;
      logo.src = '/images/Agent.png';
    });
    const logoX = (qrSize - logoSize) / 2;
    const logoY = (qrSize - logoSize) / 2;
    
    // Draw white background for logo
    ctx.fillStyle = isDark ? '#1f2937' : '#ffffff';
    ctx.fillRect(logoX - 5, logoY - 5, logoSize + 10, logoSize + 10);
    
    // Draw logo
    ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);

    return canvas.toDataURL();
  };

  const handleCopyQRCode = async () => {
    if (!qrCodeDataUrl) return;
    
    try {
      // Create a temporary canvas to get the image data
      const img = new Image();
      img.src = qrCodeDataUrl;
      
      // Wait for the image to load
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');
      
      ctx.drawImage(img, 0, 0);
      
      // Get the image as a blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to create blob from canvas'));
        });
      });
      
      // Copy the image to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob })
      ]);
      
      notify.success('QR code copied to clipboard');
    } catch (error) {
      console.error('Error copying QR code:', error);
      notify.error('Failed to copy QR code');
    }
  };

  const handleDownloadQRCode = () => {
    if (!qrCodeDataUrl) return;
    
    // Create a temporary link element
    const link = document.createElement('a');
    link.href = qrCodeDataUrl;
    link.download = `shared-conversation-${conversationId}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    notify.success('QR code downloaded');
  };

  const handleCopyLink = async () => {
    if (!sharedUrl) return;
    
    try {
      await navigator.clipboard.writeText(sharedUrl);
      notify.success('Shared URL copied to clipboard');
    } catch (error) {
      console.error('Error copying URL:', error);
      notify.error('Failed to copy URL');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        <h2 className="text-xl font-bold mb-4">Share Conversation</h2>
        
        {loading && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-4">Generating shareable link...</p>
          </div>
        )}
        
        {error && (
          <div className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg mb-4">
            {error}
          </div>
        )}
        
        {qrCodeDataUrl && !loading && !error && (
          <div className="flex flex-col items-center">
            <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
              <img 
                src={qrCodeDataUrl} 
                alt="QR Code for shared conversation" 
                className="w-64 h-64"
              />
            </div>
            
            {sharedUrl && (
              <div className="w-full mb-4 overflow-hidden">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Shareable Link:</p>
                <div className="flex items-center">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-l-lg p-2 flex-1 overflow-hidden">
                    <p className="truncate text-sm">{sharedUrl}</p>
                  </div>
                  <button 
                    onClick={handleCopyLink}
                    className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-r-lg"
                    title="Copy link"
                  >
                    <Link size={20} />
                  </button>
                </div>
              </div>
            )}
            
            <div className="flex space-x-2 w-full">
              <button
                onClick={handleCopyQRCode}
                className="flex-1 flex items-center justify-center space-x-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg py-2 px-4 transition-colors"
              >
                <Copy size={18} />
                <span>Copy QR</span>
              </button>
              
              <button
                onClick={handleDownloadQRCode}
                className="flex-1 flex items-center justify-center space-x-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg py-2 px-4 transition-colors"
              >
                <Download size={18} />
                <span>Download</span>
              </button>
            </div>
          </div>
        )}
        
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
